import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Settings, Settings2, X } from "lucide-react";
import React, {useEffect, useRef, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useNavigate, useParams} from "react-router";
import { FlaggedPathResolveView } from "../components/common/Table/File/FlaggedPathResolveView";
import UpdateCaseFlagModal from "../components/Cases/ConfirmCaseFlagUpdateModal";
import MigrationStatusChip from "../components/common/MigrationStatusChip";
import {CaseWithStatistics} from "../models/Case";
import CasesRepository from "../repositories/CasesRepository";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import {MigrationFlagStatus, MigrationFlagStatusSelectOptions,} from "../models/Flags/MigrationFlagStatus";
import {CaseFile} from "../models/CaseFile";
import {UserRole} from "../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../components/common/ActionConfirmationModal";
import ConfirmationModal from "../components/common/ConfirmationModal";
import {QUERY_KEYS} from "../constants/QueryKeys";
import {CaseFilesTable, CaseFilesTableRef} from "../components/CaseFiles/CaseFilesTable";
import {ConflictResolveView} from "../components/common/Table/File/ConflictResolveView";
import {CREATE_DELETE_FILES_REQUEST_TABLE_CONFIGS, CreateDeleteFilesRequestTable} from "../components/common/File/CreateDeleteFilesRequestTable";
import {CaseEmptyFoldersTable} from "../components/CaseEmptyFolders/CaseEmptyFoldersTable";
import {QUERY_CONFIG} from "../constants/QueryConfig";
import { DeleteCaseFilesStatusTable } from "../components/CaseFiles/DeleteCaseFilesStatusTable";
import {ExportCaseFilesToCsvStatusTable} from "../components/CaseFiles/ExportCaseFilesToCsvStatusTable";
import {ActiveJobsExplorer} from "../models/Jobs/ActiveJobsExplorer";
import {MigrationEntityShortStatistics} from "../components/common/MigrationEntity/MigrationEntityShortStatistics";
import { useManagementDrawer } from "../hooks/useManagementDrawer";
import { useDetailBreadcrumb } from "../contexts/DetailBreadcrumbContext";
import { cn } from "@/lib/utils";
import { MigrationType } from "../models/Statistics/MigrationType";
import { useRequiresReason } from "../hooks/useRequiresReason";

interface CasePageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

export const CasePage: React.FC<CasePageProps> = ({
  userRole,
  isAssignedUser
}: CasePageProps) => {
  const requiresReason = useRequiresReason();
  const { organizationId, caseId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const casesRepository = new CasesRepository();
  const caseFilesTableRef = useRef<CaseFilesTableRef>(null);
  const [caseEntity, setCase] = useState<CaseWithStatistics | undefined>(undefined);
  const [dirtyFlagStatus, setDirtyFlagStatus] = useState<MigrationFlagStatus>();
  const [isUpdateCaseFlagModalOpen, setIsUpdateCaseFlagModalOpen] = React.useState<boolean>(false);
  const [isConfirmExportModalOpen, setIsConfirmExportModalOpen] = React.useState<boolean>(false);
  const [newCaseFilesForDeletion, setNewCaseFilesForDeletion] = useState<CaseFile[]>([]);
  const [selectedTab, setSelectedTab] = useState<ActiveJobsExplorer>(ActiveJobsExplorer.Export);
  const [viewMode, setViewMode] = useState<"files" | "folders">("files");
  const queuedCount = newCaseFilesForDeletion.length;
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(queuedCount);
  const { setEntityLabel } = useDetailBreadcrumb();

  const caseQuery = useQuery({ queryKey: [QUERY_KEYS.CASE, caseId], queryFn:
    async () => await casesRepository.getCase(caseId ?? ""), gcTime: QUERY_CONFIG.DEFAULT_MIGRATION_ENTITY_CACHE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_MIGRATION_ENTITY_REFETCH_INTERVAL,
      refetchOnMount: true, });

  useEffect(() => {
    if (caseQuery.data) {
      setCase(caseQuery.data);
      setEntityLabel(`Case ${caseQuery.data.caseNumber}`);
    }
    return () => setEntityLabel(null);
  }, [caseQuery.data, caseId]);

  if (caseQuery.isLoading || (!caseQuery.isError && !caseEntity))
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-96" />
        <Skeleton className="h-[400px] w-full rounded-md" />
      </div>
    );

  if (caseQuery.isError)
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Failed to load case.</p>
        <Button variant="outline" size="sm" onClick={() => caseQuery.refetch()}>
          Retry
        </Button>
      </div>
    );

  if (!caseEntity) return null;

  const handleFlagStatusModal = (flagStatus: MigrationFlagStatus, isOpen: boolean) => {
    setIsUpdateCaseFlagModalOpen(isOpen);
    setDirtyFlagStatus(flagStatus);
  };

  const startCurrentViewExportToCsv = (reason?: string) =>
    caseFilesTableRef.current?.exportCurrentViewToCsv(reason);

  const migrationFlagStatusSelectOption =
    MigrationFlagStatusSelectOptions.find((option) => option.id === caseEntity?.flagStatus) || MigrationFlagStatusSelectOptions[0];

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="w-full my-2 gap-2 flex-wrap shrink-0">
        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-start">
            <h2 className="text-3xl font-bold tracking-tight">
              Case {caseEntity.caseNumber}{" "}
              <MigrationStatusChip status={caseEntity.status} />
            </h2>
            <div className="flex flex-row gap-2">
              {userRole !== UserRole.Viewer && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  className="gap-2"
                >
                  <Settings2 className="size-4" />
                  Manage
                  {queuedCount > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5">
                      {queuedCount}
                    </Badge>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/organization/${organizationId}/cases`)}
              >
                Back
              </Button>
            </div>
          </div>
          <div className="flex flex-row w-full flex-1 justify-between items-center gap-2">
            <MigrationEntityShortStatistics
              totalNumberOfFiles={caseEntity.statistics.totalNumberOfFiles ?? 0}
              filesMigrated={caseEntity.statistics.filesMigrated ?? 0}
              filesFlagged={caseEntity.statistics.filesFlagged ?? 0}
              filesBlockedByEntityFlag={(caseEntity.statistics.filesInEntityWithOver10KFiles ?? 0) + (caseEntity.statistics.filesInEntityBlockedByUser ?? 0)}
              filesInterruptedInitial={caseEntity.statistics.filesInterruptedInitial ?? 0}
              filesInterruptedDelta={caseEntity.statistics.filesInterruptedDelta ?? 0}
              filesModified={caseEntity.statistics.filesModified ?? 0}
              filesNotFound={caseEntity.statistics.filesNotFound ?? 0}
              filesNotFoundMarkedForRetry={caseEntity.statistics.filesNotFoundMarkedForRetry ?? 0}
              unresolvedConflictCount={caseEntity.statistics.unresolvedConflictCount ?? 0}
              acknowledgedConflictCount={caseEntity.statistics.acknowledgedConflictCount ?? 0}
              filesSizeInMb={caseEntity.statistics.filesSizeInMb ?? 0}
              filesSkippedDuplicate={caseEntity.statistics.duplicateFilesSkipped ?? 0}
            />
            <div className="flex flex-row gap-2 items-center">
              {caseEntity.flagType && (
                <Select
                  value={migrationFlagStatusSelectOption.id}
                  onValueChange={(value) => handleFlagStatusModal(value as MigrationFlagStatus, true)}
                  disabled={
                    userRole === UserRole.Viewer
                    || (!isAssignedUser && userRole !== UserRole.Admin)
                  }
                >
                  <SelectTrigger
                    className={cn(
                      "w-[260px]",
                      migrationFlagStatusSelectOption.color && colorVariantClasses[migrationFlagStatusSelectOption.color]?.text
                    )}
                  >
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {MigrationFlagStatusSelectOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        <span className={cn(option.color && colorVariantClasses[option.color]?.text)}>
                          {option.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          {viewMode === "files" ? (
            <CaseFilesTable
              ref={caseFilesTableRef}
              organizationId={organizationId ?? ""}
              caseId={caseId ?? ""}
              activeJobsExplorer={selectedTab}
              newCaseFilesForDeletion={newCaseFilesForDeletion}
              setNewCasesFilesForDeletion={setNewCaseFilesForDeletion}
              disabled={userRole == UserRole.Viewer}
              toolbar={
                <div className="flex w-full items-center gap-2 my-1">
                  <ToggleGroup
                    type="single"
                    value={viewMode}
                    onValueChange={(v) => { if (v) setViewMode(v as "files" | "folders"); }}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <ToggleGroupItem value="files" className="flex-1">Files</ToggleGroupItem>
                    <ToggleGroupItem value="folders" className="flex-1">Empty folders</ToggleGroupItem>
                  </ToggleGroup>
                  {caseEntity.hasFlaggedPaths && (
                    <FlaggedPathResolveView
                      organizationId={organizationId ?? ""}
                      migrationEntityId={caseEntity.caseNumber}
                      migrationType={MigrationType.CasesMigration}
                      entityNoun="case"
                      invalidateQueryKeys={[[QUERY_KEYS.CASE]]}
                    />
                  )}
                  {userRole !== UserRole.Viewer && (
                    <ConflictResolveView
                      parentId={caseId ?? ""}
                      entityNoun="case"
                      groupsQueryKey={[QUERY_KEYS.CASE_CONFLICT_GROUPS, caseId]}
                      invalidateQueryKeys={[[QUERY_KEYS.CASE_FILES], [QUERY_KEYS.CASE, caseId]]}
                      fetchGroups={(id, includeResolved) => casesRepository.getConflictGroups(id, includeResolved)}
                      resolveGroup={(resolutions) => casesRepository.resolveConflictGroup(resolutions)}
                    />
                  )}
                </div>
              }
            />
          ) : (
            <CaseEmptyFoldersTable
              caseId={caseId ?? ""}
              toolbar={
                <ToggleGroup
                  type="single"
                  value={viewMode}
                  onValueChange={(v) => { if (v) setViewMode(v as "files" | "folders"); }}
                  variant="outline"
                  size="sm"
                  className="w-full my-1"
                >
                  <ToggleGroupItem value="files" className="flex-1">Files</ToggleGroupItem>
                  <ToggleGroupItem value="folders" className="flex-1">Empty folders</ToggleGroupItem>
                </ToggleGroup>
              }
            />
          )}
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage files</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 p-4">
              <ToggleGroup
                type="single"
                value={String(selectedTab)}
                onValueChange={(v) => { if (v) setSelectedTab(Number(v) as ActiveJobsExplorer); }}
                variant="outline"
                size="sm"
                className="w-full my-1"
              >
                {(userRole === UserRole.Admin || isAssignedUser) && (
                  <ToggleGroupItem value={String(ActiveJobsExplorer.Delete)} className="flex-1">
                    Delete files
                  </ToggleGroupItem>
                )}
                <ToggleGroupItem value={String(ActiveJobsExplorer.Export)} className="flex-1">
                  Export CSV
                </ToggleGroupItem>
              </ToggleGroup>
              <div className="flex-1 overflow-auto min-h-0">
                {selectedTab === ActiveJobsExplorer.Delete && (userRole === UserRole.Admin || isAssignedUser) && (
                  <div className="flex flex-col h-full gap-4">
                    {newCaseFilesForDeletion.length > 0 && (
                      <>
                        <div className="flex items-center gap-3 py-2 pt-4">
                          <Separator className="flex-1" />
                          <span className="text-xs font-medium text-muted-foreground"><b>New</b></span>
                          <Separator className="flex-1" />
                        </div>
                        <CreateDeleteFilesRequestTable
                          organizationId={organizationId ?? ""}
                          entityId={caseId ?? ""}
                          entityKey={caseEntity.caseNumber}
                          newFilesForDeletion={newCaseFilesForDeletion}
                          setNewFilesForDeletion={setNewCaseFilesForDeletion}
                          fileTypeConfig={CREATE_DELETE_FILES_REQUEST_TABLE_CONFIGS.CASE_FILES_CONFIG}
                        />
                        <div className="flex items-center gap-3 py-2">
                          <Separator className="flex-1" />
                          <span className="text-xs font-medium text-muted-foreground"><b>Deletion status</b></span>
                          <Separator className="flex-1" />
                        </div>
                      </>
                    )}
                    <DeleteCaseFilesStatusTable caseId={caseId}/>
                  </div>
                )}
                {selectedTab === ActiveJobsExplorer.Export && (
                  <div className="flex flex-col h-full gap-4">
                    <div className="pt-4 w-full flex-1">
                      {requiresReason ? (
                        <ActionConfirmationModal
                          actionConfig={ACTION_CONFIGS.EXPORT_CASE_FILES_VIEW}
                          onConfirm={async (reason) => {
                            startCurrentViewExportToCsv(reason);
                          }}
                        >
                          <Button>
                            Export current files view
                          </Button>
                        </ActionConfirmationModal>
                      ) : (
                        <Button onClick={() => setIsConfirmExportModalOpen(true)}>
                          Export current files view
                        </Button>
                      )}
                      <ExportCaseFilesToCsvStatusTable
                        caseId={caseId ?? ""}
                        userRole={userRole}
                        isAssignedUser={isAssignedUser}
                        organizationId={organizationId ?? ""}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {dirtyFlagStatus && caseEntity.flagType && (
        <UpdateCaseFlagModal
          open={isUpdateCaseFlagModalOpen}
          flagStatus={dirtyFlagStatus}
          caseNumber={caseEntity.caseNumber}
          onClose={() => {handleFlagStatusModal(MigrationFlagStatus.UnResolved, false);}}
          onConfirm={async () => {
            if (caseId !== undefined) {
              await casesRepository.updateCaseFlag(caseEntity.id, dirtyFlagStatus);
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASES, organizationId] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE, caseEntity.id] });
              handleFlagStatusModal(MigrationFlagStatus.UnResolved, false);
            }
          }}
        />
      )}
      {isConfirmExportModalOpen && (
        <ConfirmationModal
          open={isConfirmExportModalOpen}
          onClose={() => {setIsConfirmExportModalOpen(false);}}
          onConfirm={async () => {
            startCurrentViewExportToCsv();
            setIsConfirmExportModalOpen(false);
          }}
          message="Are you sure you want to export case files from the current view?"
          statusColor="primary"
          buttonText="Confirm"
        />
      )}
    </div>
  );
};
